/**
 * KnowledgeTab Component
 *
 * Dedicated tab for knowledge base configuration and document management.
 * Features:
 * - Enable/disable knowledge base
 * - Hook configuration (limit, threshold)
 * - Document browser with search
 * - Document upload/create
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { BookOpen, Search, Plus, Trash2, RefreshCw, Settings, FileText, X, Upload, Link, RotateCcw } from 'lucide-react';
import { getConfigValue } from '@/config/fieldConfig';
import { knowledgeApi, type KnowledgeDocument, type DocumentListResponse } from '@/lib/api/knowledge';
import { useI18n } from '@/i18n';
import type { AgentConfiguration } from '@/types';

// Default usage guidance (synced with agents/core/hooks/knowledge/hook.py)
const DEFAULT_USAGE_GUIDANCE = `以下是与用户问题相关的知识库内容，请结合这些信息回答问题。
- 优先使用知识库中的准确信息
- 如果知识库内容与你的理解冲突，以知识库为准
- 适当引用来源可以增加回答的可信度`;

interface KnowledgeTabProps {
  config: Partial<AgentConfiguration>;
  onChange: (name: string, value: any) => void;
  appContext: any;
}

export const KnowledgeTab: React.FC<KnowledgeTabProps> = ({
  config,
  onChange,
  appContext: _appContext,
}) => {
  const { messages } = useI18n();
  const t = messages.agentConfig?.knowledge;
  const isEnabled = getConfigValue(config, 'knowledge_enabled') === true;
  const agentType = config.agent_type || 'dora';

  // Hook config from config.hooks.knowledge_context
  const knowledgeHookConfig = config.hooks?.knowledge_context || {
    enabled: false,
    limit: 5,
    threshold: 0.3,
    max_content_length: 500,
    include_metadata: false,
    usage_guidance: '',
  };

  // State
  const [documents, setDocuments] = useState<KnowledgeDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<KnowledgeDocument[] | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addMode, setAddMode] = useState<'text' | 'file' | 'url'>('text');
  const [newDocContent, setNewDocContent] = useState('');
  const [newDocMetadata, setNewDocMetadata] = useState('');
  const [newDocName, setNewDocName] = useState('');
  const [newDocDescription, setNewDocDescription] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [urlToImport, setUrlToImport] = useState('');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const pageSize = 20;

  // Load documents
  const loadDocuments = useCallback(async () => {
    if (!isEnabled) return;

    setLoading(true);
    setError(null);
    try {
      const response: DocumentListResponse = await knowledgeApi.list(agentType, {
        limit: pageSize,
        offset: page * pageSize,
      });
      setDocuments(response.documents);
      setTotal(response.total);
    } catch (err: any) {
      console.error('Failed to load documents:', err);
      setError(err.response?.data?.detail || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [agentType, isEnabled, page]);

  useEffect(() => {
    if (isEnabled) {
      loadDocuments();
    }
  }, [isEnabled, loadDocuments]);

  // Toggle knowledge - when disabling, also disable auto-retrieval hook
  const handleToggle = () => {
    const newValue = !isEnabled;
    onChange('knowledge_enabled', newValue);
    // When disabling knowledge, also disable the auto-retrieval hook
    if (!newValue && knowledgeHookConfig.enabled) {
      updateHookConfig('enabled', false);
    }
  };

  // Update hook config - directly updates parent config (no local state)
  const updateHookConfig = (field: string, value: any) => {
    const newHooksConfig = {
      ...(config.hooks || {}),
      knowledge_context: {
        ...knowledgeHookConfig,
        [field]: value,
      },
    };
    onChange('hooks', newHooksConfig);
  };

  // Search documents
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await knowledgeApi.search(agentType, searchQuery, 10);
      setSearchResults(response.documents);
    } catch (err: any) {
      console.error('Search failed:', err);
      setError(err.response?.data?.detail || 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchQuery('');
    setSearchResults(null);
  };

  // Add document
  const handleAddDocument = async () => {
    setLoading(true);
    setError(null);

    try {
      // Parse metadata if provided
      let metadata: Record<string, any> | undefined;
      if (newDocMetadata.trim()) {
        try {
          metadata = JSON.parse(newDocMetadata);
        } catch {
          setError('Invalid JSON metadata');
          setLoading(false);
          return;
        }
      }

      // Handle different add modes
      if (addMode === 'text') {
        if (!newDocContent.trim()) {
          setError('请输入文档内容');
          setLoading(false);
          return;
        }
        await knowledgeApi.create(agentType, newDocContent, metadata);
      } else if (addMode === 'file') {
        if (!selectedFile) {
          setError('请选择文件');
          setLoading(false);
          return;
        }
        await knowledgeApi.uploadFile(
          agentType,
          selectedFile,
          newDocName || selectedFile.name,
          newDocDescription,
          metadata
        );
      } else if (addMode === 'url') {
        if (!urlToImport.trim()) {
          setError('请输入URL');
          setLoading(false);
          return;
        }
        await knowledgeApi.importFromUrl(
          agentType,
          urlToImport,
          newDocName,
          newDocDescription,
          metadata
        );
      }

      // Reset form
      setNewDocContent('');
      setNewDocMetadata('');
      setNewDocName('');
      setNewDocDescription('');
      setSelectedFile(null);
      setUrlToImport('');
      setShowAddForm(false);
      loadDocuments();
    } catch (err: any) {
      console.error('Failed to add document:', err);
      setError(err.response?.data?.detail || 'Failed to add document');
    } finally {
      setLoading(false);
    }
  };

  // Delete document
  const handleDeleteDocument = async (docId: string) => {
    if (!confirm(t?.confirmDeleteDoc || 'Are you sure you want to delete this document?')) return;

    setLoading(true);
    setError(null);
    try {
      await knowledgeApi.delete(agentType, docId);
      loadDocuments();
    } catch (err: any) {
      console.error('Failed to delete document:', err);
      setError(err.response?.data?.detail || 'Failed to delete document');
    } finally {
      setLoading(false);
    }
  };

  // Clear all documents
  const handleClearAll = async () => {
    if (!confirm(t?.confirmClearAll || 'Are you sure you want to clear all documents? This cannot be undone!')) return;

    setLoading(true);
    setError(null);
    try {
      await knowledgeApi.clear(agentType);
      setDocuments([]);
      setTotal(0);
    } catch (err: any) {
      console.error('Failed to clear documents:', err);
      setError(err.response?.data?.detail || 'Failed to clear documents');
    } finally {
      setLoading(false);
    }
  };

  // Display documents (search results or all)
  const displayDocuments = searchResults ?? documents;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">知识库配置</h2>
          <p className="text-sm text-slate-500 mt-1">
            向量知识库用于上下文感知响应
          </p>
        </div>
      </div>

      {/* Enable Toggle Card */}
      <Card className="p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`p-3 rounded-xl ${isEnabled ? 'bg-green-100' : 'bg-slate-100'}`}>
              <BookOpen className={`w-6 h-6 ${isEnabled ? 'text-green-600' : 'text-slate-400'}`} />
            </div>
            <div>
              <h3 className="font-medium text-slate-900">启用知识库</h3>
              <p className="text-sm text-slate-500">
                启用后，Agent 将使用向量知识库来增强响应的上下文感知能力
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleToggle}
            className={`
              relative w-14 h-7 rounded-full transition-colors
              ${isEnabled ? 'bg-green-500' : 'bg-slate-300'}
            `}
          >
            <div
              className={`
                absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-transform
                ${isEnabled ? 'translate-x-8' : 'translate-x-1'}
              `}
            />
          </button>
        </div>

        {isEnabled && (
          <div className="mt-4 pt-4 border-t border-slate-100">
            <div className="flex items-center gap-2 text-sm text-green-600">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              知识库已启用
            </div>
          </div>
        )}
      </Card>

      {/* Hook Configuration */}
      {isEnabled && (
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <Settings className="w-5 h-5 text-slate-600" />
            <h3 className="font-medium text-slate-900">检索配置</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Hook Enabled */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                启用自动检索
              </label>
              <select
                value={knowledgeHookConfig.enabled !== false ? 'true' : 'false'}
                onChange={(e) => updateHookConfig('enabled', e.target.value === 'true')}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
              >
                <option value="true">启用</option>
                <option value="false">禁用</option>
              </select>
              <p className="text-xs text-slate-500 mt-1">对话时自动检索相关文档</p>
            </div>

            {/* Limit */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                检索数量
              </label>
              <input
                type="number"
                min={1}
                max={20}
                value={knowledgeHookConfig.limit || 5}
                onChange={(e) => updateHookConfig('limit', parseInt(e.target.value) || 5)}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
              />
              <p className="text-xs text-slate-500 mt-1">每次对话检索的文档数量</p>
            </div>

            {/* Threshold */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                相关性阈值
              </label>
              <input
                type="number"
                min={0}
                max={1}
                step={0.1}
                value={knowledgeHookConfig.threshold || 0.3}
                onChange={(e) => updateHookConfig('threshold', parseFloat(e.target.value) || 0.3)}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
              />
              <p className="text-xs text-slate-500 mt-1">最小相关性分数 (0-1)</p>
            </div>
          </div>

          {/* Usage Guidance */}
          <div className="mt-6 pt-6 border-t border-slate-200">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <label className="block text-sm font-medium text-slate-700">
                  知识库使用说明
                </label>
                {knowledgeHookConfig.usage_guidance ? (
                  <span className="px-1.5 py-0.5 text-xs bg-amber-100 text-amber-700 rounded">
                    已自定义
                  </span>
                ) : (
                  <span className="px-1.5 py-0.5 text-xs bg-slate-100 text-slate-500 rounded">
                    默认
                  </span>
                )}
              </div>
              {knowledgeHookConfig.usage_guidance && (
                <button
                  type="button"
                  onClick={() => updateHookConfig('usage_guidance', '')}
                  className="flex items-center gap-1 text-xs text-slate-500 hover:text-blue-600 transition-colors"
                  title="重置为默认说明"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  重置为默认
                </button>
              )}
            </div>
            <textarea
              value={knowledgeHookConfig.usage_guidance || DEFAULT_USAGE_GUIDANCE}
              onChange={(e) => {
                // If user clears to match default exactly, treat as empty (use default)
                const newValue = e.target.value === DEFAULT_USAGE_GUIDANCE ? '' : e.target.value;
                updateHookConfig('usage_guidance', newValue);
              }}
              className={`w-full h-32 px-3 py-2 border border-slate-300 rounded-md text-sm font-mono resize-y ${
                !knowledgeHookConfig.usage_guidance ? 'text-slate-500' : ''
              }`}
            />
            <p className="text-xs text-slate-500 mt-1">
              指导 Agent 如何使用检索到的知识库内容。将注入到系统提示词的 &lt;usage_guidance&gt; 标签中。
            </p>
          </div>
        </Card>
      )}

      {/* Document Management */}
      {isEnabled && (
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <FileText className="w-5 h-5 text-slate-600" />
              <h3 className="font-medium text-slate-900">文档管理</h3>
              <span className="text-sm text-slate-500">({total} 个文档)</span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={loadDocuments}
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                刷新
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => setShowAddForm(true)}
              >
                <Plus className="w-4 h-4 mr-1" />
                添加文档
              </Button>
              {total > 0 && (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={handleClearAll}
                  disabled={loading}
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  清空
                </Button>
              )}
            </div>
          </div>

          {/* Error display */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600">
              {error}
            </div>
          )}

          {/* Search bar */}
          <div className="flex gap-2 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="搜索文档内容..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-md text-sm"
              />
            </div>
            <Button variant="outline" size="sm" onClick={handleSearch} disabled={loading}>
              搜索
            </Button>
            {searchResults && (
              <Button variant="ghost" size="sm" onClick={handleClearSearch}>
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>

          {/* Search results indicator */}
          {searchResults && (
            <div className="mb-4 text-sm text-slate-600">
              找到 {searchResults.length} 个相关文档
            </div>
          )}

          {/* Add document form */}
          {showAddForm && (
            <div className="mb-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
              <h4 className="font-medium text-slate-900 mb-3">添加新文档</h4>

              {/* Mode Tabs */}
              <div className="flex gap-2 mb-4">
                <button
                  type="button"
                  onClick={() => setAddMode('text')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    addMode === 'text'
                      ? 'bg-blue-500 text-white'
                      : 'bg-white text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <FileText className="w-4 h-4" />
                  文本
                </button>
                <button
                  type="button"
                  onClick={() => setAddMode('file')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    addMode === 'file'
                      ? 'bg-blue-500 text-white'
                      : 'bg-white text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <Upload className="w-4 h-4" />
                  文件上传
                </button>
                <button
                  type="button"
                  onClick={() => setAddMode('url')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    addMode === 'url'
                      ? 'bg-blue-500 text-white'
                      : 'bg-white text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <Link className="w-4 h-4" />
                  URL导入
                </button>
              </div>

              <div className="space-y-3">
                {/* Text Mode */}
                {addMode === 'text' && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      文档内容 *
                    </label>
                    <textarea
                      value={newDocContent}
                      onChange={(e) => setNewDocContent(e.target.value)}
                      placeholder="输入文档内容..."
                      rows={4}
                      className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                    />
                  </div>
                )}

                {/* File Upload Mode */}
                {addMode === 'file' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        选择文件 * (支持 PDF, DOCX, TXT, MD, JSON, CSV)
                      </label>
                      <input
                        type="file"
                        accept=".pdf,.docx,.txt,.md,.json,.csv"
                        onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                        className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                      />
                      {selectedFile && (
                        <p className="mt-1 text-xs text-slate-500">
                          已选择: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        文档名称 (可选)
                      </label>
                      <input
                        type="text"
                        value={newDocName}
                        onChange={(e) => setNewDocName(e.target.value)}
                        placeholder="默认使用文件名"
                        className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        描述 (可选)
                      </label>
                      <input
                        type="text"
                        value={newDocDescription}
                        onChange={(e) => setNewDocDescription(e.target.value)}
                        placeholder="文档描述"
                        className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                      />
                    </div>
                  </>
                )}

                {/* URL Import Mode */}
                {addMode === 'url' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        URL地址 *
                      </label>
                      <input
                        type="url"
                        value={urlToImport}
                        onChange={(e) => setUrlToImport(e.target.value)}
                        placeholder="https://example.com/document"
                        className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        文档名称 (可选)
                      </label>
                      <input
                        type="text"
                        value={newDocName}
                        onChange={(e) => setNewDocName(e.target.value)}
                        placeholder="文档名称"
                        className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        描述 (可选)
                      </label>
                      <input
                        type="text"
                        value={newDocDescription}
                        onChange={(e) => setNewDocDescription(e.target.value)}
                        placeholder="文档描述"
                        className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                      />
                    </div>
                  </>
                )}

                {/* Common fields for all modes */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    元数据 (JSON, 可选)
                  </label>
                  <textarea
                    value={newDocMetadata}
                    onChange={(e) => setNewDocMetadata(e.target.value)}
                    placeholder='{"category": "astrology", "topic": "zodiac"}'
                    rows={2}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm font-mono"
                  />
                </div>

                <div className="flex justify-end gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowAddForm(false);
                      setNewDocContent('');
                      setNewDocMetadata('');
                      setNewDocName('');
                      setNewDocDescription('');
                      setSelectedFile(null);
                      setUrlToImport('');
                    }}
                  >
                    取消
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleAddDocument}
                    disabled={
                      loading ||
                      (addMode === 'text' && !newDocContent.trim()) ||
                      (addMode === 'file' && !selectedFile) ||
                      (addMode === 'url' && !urlToImport.trim())
                    }
                  >
                    添加
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Documents list */}
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {loading && displayDocuments.length === 0 ? (
              <div className="text-center py-8 text-slate-500">加载中...</div>
            ) : displayDocuments.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                {searchResults ? '没有找到相关文档' : '暂无文档，点击"添加文档"开始'}
              </div>
            ) : (
              displayDocuments.map((doc) => (
                <div
                  key={doc.doc_id}
                  className="p-3 bg-white border border-slate-200 rounded-lg hover:border-slate-300 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-700 line-clamp-2">
                        {doc.content}
                      </p>
                      <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                        <span className="font-mono">{doc.doc_id.slice(0, 8)}...</span>
                        {doc.relevance_score !== undefined && (
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                            相关性: {(doc.relevance_score * 100).toFixed(0)}%
                          </span>
                        )}
                        {doc.metadata && Object.keys(doc.metadata).length > 0 && (
                          <span className="text-slate-400">
                            元数据: {Object.keys(doc.metadata).join(', ')}
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeleteDocument(doc.doc_id)}
                      className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                      title="删除文档"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Pagination */}
          {!searchResults && total > pageSize && (
            <div className="flex items-center justify-center gap-2 mt-4 pt-4 border-t border-slate-100">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                上一页
              </Button>
              <span className="text-sm text-slate-600">
                第 {page + 1} / {Math.ceil(total / pageSize)} 页
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setPage((p) => p + 1)}
                disabled={(page + 1) * pageSize >= total}
              >
                下一页
              </Button>
            </div>
          )}
        </Card>
      )}

      {/* Help Text */}
      <Card className="p-4 bg-slate-50 border-slate-200">
        <h4 className="text-sm font-medium text-slate-700 mb-2">知识库说明</h4>
        <ul className="text-xs text-slate-600 space-y-1">
          <li>• 知识库使用向量数据库存储和检索相关信息</li>
          <li>• 启用后，Agent 会根据用户问题自动检索相关知识</li>
          <li>• 检索到的知识会作为上下文注入到提示词中</li>
          <li>• 可以通过检索配置调整检索数量和相关性阈值</li>
          <li>• 支持三种导入方式：文本输入、文件上传 (PDF/DOCX/TXT/MD/JSON/CSV)、URL导入</li>
          <li>• 支持语义搜索、手动添加和删除文档</li>
        </ul>

        {/* Auto Retrieval Logic */}
        <h4 className="text-sm font-medium text-slate-700 mt-4 mb-2">自动检索逻辑</h4>
        <ul className="text-xs text-slate-600 space-y-1">
          <li>• <strong>查询来源</strong>：使用当前用户消息进行检索（不包含历史消息）</li>
          <li>• <strong>文本长度</strong>：查询文本截断至 250 字符（短文本 Embedding 效果更好）</li>
          <li>• <strong>最小长度</strong>：文本少于 10 字符时跳过检索（如 "看看"、"好的"）</li>
          <li>• <strong>多模态支持</strong>：自动提取图片消息中的文本部分进行检索</li>
          <li>• <strong>纯图片</strong>：无文本内容时跳过检索</li>
        </ul>

        {/* Injected Prompt Structure */}
        <h4 className="text-sm font-medium text-slate-700 mt-4 mb-2">注入的 Prompt 结构</h4>
        <pre className="text-xs bg-slate-100 p-3 rounded-md overflow-x-auto text-slate-700">
{`<knowledge_context>
  <usage_guidance>
    [知识库使用说明内容]
  </usage_guidance>
  <documents>
    <document relevance="0.95">
      文档内容 1...
    </document>
    <document relevance="0.82">
      文档内容 2...
    </document>
  </documents>
</knowledge_context>`}
        </pre>
      </Card>
    </div>
  );
};

export default KnowledgeTab;
